import { useContext, useEffect, useMemo, useRef } from "react";
import { AdvertiserContentLanguageContext } from "../contexts/AdvertiserContentLanguageContext";
import {
  ensureContentLanguageField,
  getContentLanguageOptions,
  resolveContentLanguage,
} from "../utils/catalogs/contentLanguage";

export const useAdvertiserContentLanguage = ({ preset } = {}) => {
  const value = useContext(AdvertiserContentLanguageContext);
  if (!value) {
    throw new Error(
      "useAdvertiserContentLanguage must be used within AdvertiserContentLanguageProvider"
    );
  }

  const options = useMemo(
    () => (preset ? getContentLanguageOptions({ preset }) : []),
    [preset]
  );

  const resolvedLanguage = useMemo(() => {
    if (!preset) return null;

    return resolveContentLanguage({
      storedLanguage: value.storedLanguage,
      preset,
      platformLanguage: value.platformLanguage,
    });
  }, [preset, value.platformLanguage, value.storedLanguage]);

  return {
    ...value,
    options,
    resolvedLanguage,
  };
};

export const useEnsureContentLanguageField = ({
  preset,
  setValue,
  getValues,
  contextLanguage = null,
  enabled = true,
  fieldName = "language",
}) => {
  const { storedLanguage, isLoaded, platformLanguage } =
    useAdvertiserContentLanguage();
  const sessionRef = useRef({
    applied: false,
    stored: undefined,
    preset: undefined,
  });

  useEffect(() => {
    if (!enabled) {
      sessionRef.current = {
        applied: false,
        stored: undefined,
        preset: undefined,
      };
      return;
    }
    if (!isLoaded) return;

    const currentValue = getValues(fieldName);
    const session = sessionRef.current;
    const storedArrivedLate =
      session.applied && !session.stored && storedLanguage;

    if (!session.applied || storedArrivedLate) {
      const next = ensureContentLanguageField({
        value: contextLanguage,
        storedLanguage,
        preset,
        platformLanguage,
      });
      if (next !== currentValue) {
        setValue(fieldName, next);
      }
      sessionRef.current = {
        applied: true,
        stored: storedLanguage,
        preset,
      };
      return;
    }

    if (session.preset !== preset) {
      const next = ensureContentLanguageField({
        value: currentValue,
        storedLanguage,
        preset,
        platformLanguage,
      });
      if (next !== currentValue) {
        setValue(fieldName, next);
      }
      sessionRef.current = { ...session, preset };
    }
  }, [
    contextLanguage,
    enabled,
    fieldName,
    getValues,
    isLoaded,
    platformLanguage,
    preset,
    setValue,
    storedLanguage,
  ]);
};
